import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ArrowLeft, Flag, Trophy, ArrowsLeftRight, TennisBall } from "@phosphor-icons/react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import WinnerCelebration from "../components/WinnerCelebration";
import ServeTossDialog from "../components/ServeTossDialog";

function teamName(t) { return t.p2 ? `${t.p1} / ${t.p2}` : t.p1; }

function TeamControl({ team, label, accent, players, point, onAdd, onSub, disabled, serving, servingPlayer }) {
  const isGreen = accent === "green";
  const color = isGreen ? "#CCFF00" : "#00D1FF";
  const effPlayer = serving ? (servingPlayer === 2 && !players.p2 ? 1 : servingPlayer) : null;
  const line = (name, idx) => (
    <span className="flex items-center gap-2 min-w-0">
      {effPlayer === idx && <TennisBall data-testid={`serving-team${team}`} size={20} weight="fill" className="text-padel-green shrink-0 animate-pulse-glow" />}
      <span className="truncate">{name}</span>
    </span>
  );
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-5">
        <p className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color }}>{label}</p>
        <h2 className="font-heading text-xl sm:text-3xl font-extrabold uppercase leading-tight min-w-0 flex flex-col gap-0.5">
          {line(players.p1, 1)}
          {players.p2 && line(players.p2, 2)}
        </h2>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center relative">
        <AnimatePresence mode="popLayout">
          <motion.span
            key={point}
            data-testid={`points-team${team}`}
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: [1.25, 1], opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="font-heading font-extrabold leading-none text-[26vw] sm:text-[16vw]"
            style={{ color, textShadow: `0 0 30px ${color}cc` }}
          >
            {point}
          </motion.span>
        </AnimatePresence>
      </div>
      <div className="grid grid-cols-3 gap-px bg-white/10">
        <button
          data-testid={`sub-point-team${team}`}
          onClick={onSub}
          disabled={disabled}
          className="col-span-1 h-24 flex items-center justify-center bg-[#0A0A0C] text-padel-danger active:bg-padel-danger active:text-black transition-colors disabled:opacity-30"
        >
          <Minus size={36} weight="bold" />
        </button>
        <button
          data-testid={`add-point-team${team}`}
          onClick={onAdd}
          disabled={disabled}
          className="col-span-2 h-24 flex items-center justify-center text-black font-heading text-2xl uppercase tracking-widest active:brightness-110 transition disabled:opacity-30"
          style={{ background: color }}
        >
          <Plus size={32} weight="bold" className="mr-1" /> Punto
        </button>
      </div>
    </div>
  );
}

export default function Control() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [swapped, setSwapped] = useState(false);
  const prevWinner = useRef(null);

  const load = useCallback(() => api.get(`/matches/${matchId}`).then((r) => setMatch(r.data)), [matchId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (match?.score?.winner && prevWinner.current !== match.score.winner) {
      prevWinner.current = match.score.winner;
      confetti({ particleCount: 160, spread: 90, origin: { y: 0.5 }, colors: ["#CCFF00", "#00D1FF", "#ffffff"] });
    }
  }, [match]);

  const point = async (team, delta) => {
    try {
      const { data } = await api.post(`/matches/${matchId}/point`, { team, delta });
      setMatch(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const finish = async () => {
    await api.post(`/matches/${matchId}/finish`);
    toast.success("Partido finalizado");
    navigate("/");
  };

  const setServer = async (server) => {
    try {
      const { data } = await api.post(`/matches/${matchId}/server`, { server });
      setMatch(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!match) return <div className="min-h-screen flex items-center justify-center text-padel-blue font-heading uppercase animate-pulse-glow">Cargando…</div>;

  const s = match.score;
  const finished = match.status === "finished" || s.winner;
  const needsServe = match.status === "live" && !match.server && !finished;

  return (
    <div className="min-h-screen flex flex-col bg-[#0A0A0C] grain relative">
      {/* top bar */}
      <div className="relative flex items-center justify-between px-4 h-14 border-b border-white/10 shrink-0 z-10">
        <button data-testid="back-btn" onClick={() => navigate("/")} className="flex items-center gap-1 text-muted-foreground hover:text-white text-sm">
          <ArrowLeft size={18} /> <span className="hidden sm:inline">Volver</span>
        </button>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
          <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{match.court_name}</p>
          <p className="font-heading uppercase tracking-widest text-padel-green text-sm flex items-center justify-center gap-2">
            <span className="w-2 h-2 rounded-full bg-padel-green animate-pulse-glow" /> En vivo
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            data-testid="swap-sides-btn"
            onClick={() => setSwapped((v) => !v)}
            variant="ghost"
            className="text-padel-blue hover:text-padel-blue hover:bg-padel-blue/10 text-sm font-heading uppercase tracking-widest px-2 lg:px-3"
          >
            <ArrowsLeftRight size={18} className="lg:mr-1" /> <span className="hidden lg:inline">Cambio de cancha</span>
          </Button>
          <Button data-testid="finish-match-btn" onClick={finish} variant="ghost" className="text-padel-danger hover:text-padel-danger hover:bg-padel-danger/10 text-sm px-2 lg:px-3">
            <Flag size={16} className="lg:mr-1" /> <span className="hidden lg:inline">Finalizar</span>
          </Button>
        </div>
      </div>

      {/* sets/games summary */}
      <div className="flex items-center justify-center gap-8 py-3 border-b border-white/10 shrink-0">
        <ScorePill label="Sets" v1={s.sets1} v2={s.sets2} />
        <ScorePill label={s.in_tiebreak ? "Tie-break" : "Games"} v1={s.games1} v2={s.games2} />
      </div>

      {finished && (
        <div className="bg-padel-green/10 border-b border-padel-green text-padel-green py-3 text-center font-heading uppercase tracking-widest flex items-center justify-center gap-2">
          <Trophy size={20} weight="fill" /> Ganador: {teamName(s.winner === 1 ? match.team1 : match.team2)}
        </div>
      )}

      {/* controls */}
      <div className="flex-1 grid grid-cols-2 divide-x divide-white/10">
        {(() => {
          const t1 = (
            <TeamControl key="t1" team={1} label="Equipo 1" accent="blue" players={match.team1} point={s.points1} disabled={finished} serving={!finished && s.serving === 1} servingPlayer={s.serving_player}
              onAdd={() => point(1, 1)} onSub={() => point(1, -1)} />
          );
          const t2 = (
            <TeamControl key="t2" team={2} label="Equipo 2" accent="green" players={match.team2} point={s.points2} disabled={finished} serving={!finished && s.serving === 2} servingPlayer={s.serving_player}
              onAdd={() => point(2, 1)} onSub={() => point(2, -1)} />
          );
          return swapped ? [t2, t1] : [t1, t2];
        })()}
      </div>

      {needsServe && (
        <ServeTossDialog team1={match.team1} team2={match.team2} onSelect={setServer} />
      )}

      {s.winner && (
        <WinnerCelebration
          team={s.winner === 1 ? match.team1 : match.team2}
          accent={s.winner === 1 ? "blue" : "green"}
          size="lg"
        >
          <Button
            data-testid="winner-back-btn"
            onClick={() => navigate("/")}
            className="bg-padel-blue text-black font-heading text-lg uppercase tracking-widest rounded-none hover:bg-padel-green px-8 h-12"
          >
            Volver al panel
          </Button>
        </WinnerCelebration>
      )}
    </div>
  );
}

function ScorePill({ label, v1, v2 }) {
  return (
    <div className="text-center">
      <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1">{label}</p>
      <div className="font-heading text-2xl font-extrabold tabular-nums">
        <span className="text-padel-blue">{v1}</span>
        <span className="text-muted-foreground mx-1">·</span>
        <span className="text-padel-green">{v2}</span>
      </div>
    </div>
  );
}
